import styled from '@emotion/styled';
import { ColumnSelector } from './columnSelector/ColumnSelector';
import { Plus } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useTableContext } from 'features/misc/table/providers/TableProvider';
import { Filter } from './filter/Filter';
import { Search } from './Search';
import { Selected } from './Selected';
import { Button, ButtonProps } from 'components';
import { Task } from 'features/taskboard/types/Tasks';
import type { SelectedOptions } from '../types';
import { Dropdown } from './DropDown';
import { useUser } from 'features/auth';
import { Box, Divider } from '@mui/material';
import { SelectedRowCount } from './SelectedRowCount';
import { ActionsPopup } from './ActionsPopup';
import { useState } from 'react';
import { SettingsButton } from './SettingsButton';
import { useFeatureFlag } from 'features/accessControl';
import { ViewDropdown } from './views/ViewDropdown';
import type { ViewConfig, View } from 'types/views';

export type HeaderProps = {
  addButtonName?: string;
  rowSelectionCount?: number;
  filterTitle?: React.ReactNode;
  tableId?: string;
  tableName?: string;
  addButtonProps?: ButtonProps;
  filters?: import('types/table').FilterParams;
  viewConfig?: ViewConfig;
  onViewChange?: (view: View | null) => void;
  /** When true, selection count and bulk actions are shown in floating bar; hide them from header */
  useFloatingBulkBar?: boolean;
};

export const Header = ({
  addButtonName,
  filterTitle,
  rowSelectionCount,
  tableId,
  tableName,
  addButtonProps,
  filters,
  viewConfig,
  onViewChange,
  useFloatingBulkBar,
}: HeaderProps) => {
  const { table, globalFilter, config, variant = 'default' } = useTableContext();
  const { data: userData } = useUser();
  const [searchOpen, setSearchOpen] = useState(Boolean(table.options.meta?.tableState?.search));
  const isExportEnabled = useFeatureFlag('ExportDataSupplierManagement');
  const isRowSelectionIds = Object.keys(table.getState().rowSelection).length > 0;
  const isSearchOpen =
    searchOpen ||
    Boolean(
      table.options.meta?.onTableStateChange
        ? table.options.meta.tableState?.search ?? ''
        : globalFilter ?? ''
    );

  const loggedInUser = userData?.loginContext;
  const handleHeaderOptionClick = (selectedOption: SelectedOptions) => {
    const selectedRows = table.getSelectedRowModel()?.flatRows;
    const rowSelection = Object.keys(table.getState().rowSelection);
    if (selectedRows) {
      const tasks = selectedRows.map(row => {
        const taskRow = row.original as Task;
        return taskRow;
      });
      if (selectedOption === 'read' || selectedOption === 'unread') {
        // Filter rows based on the action to avoid toggle behavior issues:
        // - "Mark as Read": only send unread tasks (viewedOn === null)
        // - "Mark as Unread": only send read tasks (viewedOn !== null)
        const filteredRows = selectedRows.filter(row => {
          const task = row.original as Task;
          if (selectedOption === 'read') {
            // For "Mark as Read", only include unread tasks
            return task.viewedOn === null;
          }
          // For "Mark as Unread", only include read tasks
          return task.viewedOn !== null;
        });
        if (filteredRows.length > 0) {
          table.options.meta?.onHeaderClick(
            [],
            selectedOption,
            filteredRows.map(row => row.index)
          );
        }
      } else {
        table.options.meta?.onHeaderClick(tasks, selectedOption, rowSelection);
      }
      table.resetRowSelection();
    }
  };
  const getHeaderOptions = (): SelectedOptions[] => {
    const selectedRows = table.getSelectedRowModel()?.flatRows;
    //let selectedOptions
    if (selectedRows) {
      const tasks = selectedRows.map(row => {
        const taskRow = row.original as Task;
        return taskRow;
      });

      // Check if any selected task is not assigned to the logged-in user
      const hasTasksNotOwnedByUser = tasks.some(t => t.assignedToPerson?.id !== loggedInUser?.id);
      // If any task is not owned by the user, hide read/unread options
      if (hasTasksNotOwnedByUser) {
        return table.options.meta?.selectedOptions.filter(
          o => o !== 'unread' && o !== 'read'
        ) as SelectedOptions[];
      }
      const haveUnreadTask = tasks.filter(
        t => t.viewedOn == null && t.assignedToPerson?.id === loggedInUser?.id
      );
      const haveReadTask = tasks.filter(t => t.viewedOn != null);
      if (haveReadTask?.length === 0 && haveUnreadTask?.length === 0) {
        return table.options.meta?.selectedOptions.filter(
          o => o !== 'unread' && o !== 'read'
        ) as SelectedOptions[];
      }
      if (
        (haveReadTask?.length > 0 && haveUnreadTask?.length > 0) ||
        (haveReadTask.length === 0 && haveUnreadTask?.length > 0)
      ) {
        return table.options.meta?.selectedOptions.filter(o => o !== 'unread') as SelectedOptions[];
      }
      if (haveReadTask?.length > 0 && haveUnreadTask?.length === 0) {
        return table.options.meta?.selectedOptions.filter(o => o !== 'read') as SelectedOptions[];
      }
      return table.options.meta?.selectedOptions as SelectedOptions[];
    }
    return table.options.meta?.selectedOptions as SelectedOptions[];
  };

  const showHeaderSelection = !useFloatingBulkBar;

  const onAddButtonClick = () => {
    if (config.event) {
      config.event('addRow', { source_location: '' });
    }
  };

  return (
    <Container $variant={variant}>
      <Left>
        {showHeaderSelection && (
          <>
            <SelectedRowCount tableId={tableId} rowSelectionCount={rowSelectionCount} />
            <StyledDivider
              data-testid={`table_${tableId}_selection_count_divider`}
              orientation="vertical"
            />
          </>
        )}
        <ColumnSelector />
        {config?.hideFilter ? null : <Filter title={filterTitle} />}
        {isExportEnabled && config?.settingsActions && (
          <SettingsButton
            settingsActions={config?.settingsActions}
            tableId={tableId}
            tableState={table.options.meta?.tableState}
            columns={table.getAllColumns().map(col => ({ id: col.id }))}
            filters={filters}
          />
        )}
        {showHeaderSelection &&
          table.options?.meta?.selectedOptions?.length &&
          isRowSelectionIds && (
            <MiddleMobile>
              <ActionsPopup>
                <MiddlePopup>
                  {isRowSelectionIds ? (
                    <Selected options={getHeaderOptions()} onClick={handleHeaderOptionClick} />
                  ) : null}
                </MiddlePopup>
              </ActionsPopup>
            </MiddleMobile>
          )}
      </Left>
      {showHeaderSelection && (
        <Middle>
          {table.options?.meta?.selectedOptions?.length && isRowSelectionIds ? (
            <Selected options={getHeaderOptions()} onClick={handleHeaderOptionClick} />
          ) : null}
        </Middle>
      )}
      <Right>
        {addButtonName && typeof table.options?.meta?.addRow === 'string' ? (
          <Link to={table.options.meta.addRow} onClick={() => onAddButtonClick()}>
            {addButtonProps ? (
              <Button {...addButtonProps} data-testid={`table_${tableId}_add_button`} />
            ) : (
              <Button variant="primary" data-testid={`table_${tableId}_add_button`}>
                <Plus size={16} color="white" />
                {addButtonName}
              </Button>
            )}
          </Link>
        ) : null}
        {(addButtonName || addButtonProps) && typeof table.options?.meta?.addRow !== 'string' ? (
          addButtonProps ? (
            <Button
              {...addButtonProps}
              onClick={e => {
                onAddButtonClick();
                addButtonProps.onClick?.(e);
              }}
              data-testid={`table-${tableId}-add-button`}
            />
          ) : (
            <Button
              variant="primary"
              size="lg"
              startIcon={<Plus size={16} color="#FFF" />}
              onClick={() => {
                table.options?.meta?.addRow('topright');
                onAddButtonClick();
              }}
              data-testid={`table-${tableId}-add-button`}
            >
              {addButtonName}
            </Button>
          )
        ) : null}
        <Dropdown />
        {viewConfig && tableName && (
          <ViewDropdown viewConfig={viewConfig} tableName={tableName} onViewChange={onViewChange} />
        )}
        {config?.hideSearch ? null : (
          <Search searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
        )}
      </Right>
    </Container>
  );
};
const Container = styled.section<{ $variant?: 'default' | 'transparent' }>`
  display: flex;
  padding: 6px 0;
  margin-bottom: 16px;
  width: 100%;
  height: 44px;
  flex-shrink: 0;
  box-sizing: border-box;
  align-items: center;
  overflow: hidden;
  position: relative;
  justify-content: space-between;
  container-type: inline-size;
  background-color: ${p => (p.$variant === 'transparent' ? 'transparent' : 'inherit')};
`;

export const List = styled.ul`
  padding: 0;
  margin: 0;
  display: flex;
  list-style: none;
  gap: 0.5rem;
`;

export const Left = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  gap: 8px;
`;

const Middle = styled(Box)`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
  justify-content: center;
  min-width: 0;
  @container (max-width: 1370px) {
    display: none;
  }
`;

const MiddleMobile = styled.div`
  display: none;
  @container (max-width: 1370px) {
    display: flex;
  }
`;

const MiddlePopup = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  align-items: flex-start;
  button {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  & input[type='checkbox'] {
    display: initial;
  }
`;

const StyledDivider = styled(Divider)`
  border-width: 1px;
  border-color: ${p => p.theme.palette.border.primarySubtle};
  height: 24px;
`;
